import { useState } from "react";
import { Factory, Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Overline } from "./primitives";
import { OtpField } from "./OtpField";
import { isValidEmail, validatePhone, cleanPhone } from "../../lib/validate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full bg-gf-bg border border-white/15 px-4 py-3.5 font-body text-sm text-gf-text placeholder:text-gf-muted focus:border-gf-primary focus:outline-none";

const INTEREST_OPTIONS = [
  { label: "Solar + Storage", tag: "re-storage-advisory" },
  { label: "Open Access / PPA", tag: "green-energy-ppa" },
  { label: "EPC & Construction", tag: "epc" },
  { label: "Green Financing", tag: "green-financing" },
  { label: "Data Centers", tag: "data-centers-energy-management" },
  { label: "Transmission / Grid", tag: "transmission-engineering" },
];

export const CiLeadCapture = () => {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "" });
  const [interests, setInterests] = useState([]);
  const [otpToken, setOtpToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const toggleInterest = (tag) =>
    setInterests((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));

  const submit = async (e) => {
    e.preventDefault();
    if (!isValidEmail(form.email)) { toast.error("Enter a valid email, e.g. abc@gmail.com"); return; }
    const phoneErr = validatePhone(form.phone);
    if (phoneErr) { toast.error(phoneErr); return; }
    if (!otpToken) { toast.error("Please verify your email with the OTP first"); return; }
    const phone = cleanPhone(form.phone);
    setBusy(true);
    try {
      const res = await fetch(`${API}/ci-leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: form.name, company: form.company, email: form.email, phone, interests, otp_token: otpToken }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Something went wrong");
      setDone(true);
      toast.success("You're on the list — expect regular energy-savings updates.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="ci-lead" className="relative z-10 py-20 md:py-28 border-t border-white/5">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <Reveal>
          <div className="relative overflow-hidden bg-gf-surface border border-gf-primary/20 p-8 md:p-12">
            <div className="absolute -top-20 -right-16 w-[32vw] h-[32vw] max-w-[400px] rounded-full bg-gf-primary/15 blur-[110px]" />
            <div className="relative z-10 grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-6">
                <Overline>For C&I Consumers</Overline>
                <h2 className="font-heading font-black text-gf-text text-3xl sm:text-4xl md:text-5xl leading-[0.95] tracking-tight mt-5">
                  Are you a <span className="text-gf-primary">C&I customer?</span>
                </h2>
                <p className="font-body text-gf-muted text-base leading-relaxed mt-5 max-w-lg">
                  Looking for avenues to reduce your energy bill and improve reliability? Join our list —
                  we'll send you regular, no-fluff updates on savings opportunities, solar + storage economics
                  and the regulatory moves that affect your power cost.
                </p>
                <div className="flex items-center gap-2.5 mt-6 font-mono text-xs text-gf-muted">
                  <Factory className="w-4 h-4 text-gf-primary" /> Industrial · Commercial · Data Centers · Institutions
                </div>
              </div>

              <div className="lg:col-span-6">
                {done ? (
                  <div data-testid="ci-lead-success" className="bg-gf-bg border border-gf-primary/30 p-8 text-center">
                    <CheckCircle2 className="w-10 h-10 text-gf-primary mx-auto" />
                    <p className="font-heading font-bold text-gf-text text-2xl mt-4">You're on the list.</p>
                    <p className="font-body text-sm text-gf-muted mt-3">
                      Expect regular energy-savings updates. Want an instant answer?{" "}
                      <a href="#bess" className="text-gf-primary underline">Try the Bill Optimiser</a>.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={submit} data-testid="ci-lead-form" className="bg-gf-bg border border-white/10 p-6 md:p-8">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <input required data-testid="ci-name-input" placeholder="Your name *" value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
                      <input required data-testid="ci-company-input" placeholder="Company name *" value={form.company}
                        onChange={(e) => setForm({ ...form, company: e.target.value })} className={inputCls} />
                      <input required type="email" data-testid="ci-email-input" placeholder="Work email *" value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
                      <input required data-testid="ci-phone-input" placeholder="Mobile, e.g. +91 98765 43210 *" value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
                    </div>
                    <div className="mt-4">
                      <p className="font-mono text-[10px] uppercase tracking-wider text-gf-muted">Interest areas (optional — tailors your regulatory alerts)</p>
                      <div className="flex flex-wrap gap-2 mt-2.5">
                        {INTEREST_OPTIONS.map((o) => (
                          <button key={o.tag} type="button" data-testid={`ci-interest-${o.tag}`}
                            onClick={() => toggleInterest(o.tag)}
                            className={`border px-3 py-1.5 font-body text-xs transition-colors ${
                              interests.includes(o.tag)
                                ? "border-gf-primary text-gf-primary bg-gf-primary/10"
                                : "border-white/15 text-gf-muted hover:text-gf-text"}`}>
                            {o.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="mt-4">
                      <OtpField email={form.email} onToken={setOtpToken} />
                    </div>
                    <button type="submit" disabled={busy} data-testid="ci-submit-btn"
                      className="mt-5 w-full inline-flex items-center justify-center gap-2.5 bg-gf-primary text-gf-bg font-body font-bold px-7 py-4 hover:bg-gf-primary-hover transition-colors disabled:opacity-60">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Get savings updates <ArrowRight className="w-4 h-4" /></>}
                    </button>
                    <p className="font-body text-[11px] text-gf-muted mt-3 text-center">
                      No spam — a regular digest of savings avenues and regulatory updates. Unsubscribe anytime.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
